import { useState, useEffect } from 'react';
import { 
  Plus, Pencil, Trash2, X, UserCheck, Search, Check, AlertCircle, Loader2
} from 'lucide-react';
import axios from 'axios';
import { detectPerms } from '../../permissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const initialFormData = {
  name: '', email: '', phone: '', gst_number: '',
  registration: 'Registered',
  shipping_address: '', billing_address: '', active: true,
  _also_create_vendor: false,
};

export const Customers = () => {
  const perms = detectPerms();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [searchQuery, setSearchQuery] = useState('');
  const [registrationFilter, setRegistrationFilter] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  useEffect(() => { fetchItems(); }, []);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${BACKEND_URL}/api/customers`, getAuthHeaders());
      setItems(res.data);
    } catch (err) { setError('Failed to fetch customers'); }
    finally { setLoading(false); }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const openAddModal = (prefillName = '') => {
    setFormData({ ...initialFormData, name: prefillName }); setEditingItem(null); setError(''); setShowModal(true);
  };

  const openEditModal = (item) => {
    setFormData({
      name: item.name || '', email: item.email || '', phone: item.phone || '',
      gst_number: item.gst_number || '',
      registration: item.registration || 'Registered',
      shipping_address: item.shipping_address || '',
      billing_address: item.billing_address || '', active: item.active ?? true
    });
    setEditingItem(item); setError(''); setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setError('');
    if (!formData.name.trim()) { setError('Name is required'); return; }

    // Unique-phone guard (only when a phone is entered)
    const phone = (formData.phone || '').trim();
    if (phone) {
      const dup = items.find(c =>
        (c.phone || '').trim() === phone &&
        (!editingItem || c.id !== editingItem.id)
      );
      if (dup) {
        setError(`A customer with this phone number already exists${dup.name ? ` (${dup.name})` : ''}`);
        return;
      }
    }

    setSubmitting(true);
    try {
      const payload = { ...formData };
      delete payload._also_create_vendor;
      // Clear GST for unregistered
      if (payload.registration === 'Unregistered') payload.gst_number = null;
      ['email', 'phone', 'gst_number', 'shipping_address', 'billing_address'].forEach(k => {
        if (!payload[k]?.trim()) payload[k] = null;
      });
      if (editingItem) {
        await axios.put(`${BACKEND_URL}/api/customers/${editingItem.id}`, payload, getAuthHeaders());
      } else {
        await axios.post(`${BACKEND_URL}/api/customers`, payload, getAuthHeaders());
        // Mirror to vendors if requested
        if (formData._also_create_vendor) {
          try { await axios.post(`${BACKEND_URL}/api/vendors`, payload, getAuthHeaders()); }
          catch (e) { /* non-fatal — customer was created */ }
        }
      }
      setShowModal(false); fetchItems();
    } catch (err) { setError(err.response?.data?.detail || 'Failed to save customer'); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/customers/${id}`, getAuthHeaders());
      setDeleteConfirm(null); fetchItems();
    } catch (err) { setError('Failed to delete customer'); }
  };

  const filtered = items.filter(i => {
    const q = searchQuery.toLowerCase();
    const matchesSearch = !q ||
      i.name?.toLowerCase().includes(q) ||
      i.email?.toLowerCase().includes(q) ||
      i.phone?.includes(q) ||
      i.gst_number?.toLowerCase().includes(q);
    const matchesReg = !registrationFilter || (i.registration || 'Registered') === registrationFilter;
    return matchesSearch && matchesReg;
  });

  return (
    <div data-testid="customers-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight">Customers</h1>
          <p className="text-slate-600 mt-1">Manage customers</p>
        </div>
        <button onClick={() => openAddModal()} data-testid="add-customer-btn"
          className="inline-flex items-center gap-2 bg-orange-500 text-white font-bold tracking-wider uppercase px-6 py-3 rounded-none hover:bg-orange-600 transition-colors">
          <Plus className="h-5 w-5" /> Add Customer
        </button>
      </div>

      <div className="bg-white rounded-sm border border-slate-200 p-4 mb-6">
        <div className="flex gap-3 items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <input type="text" placeholder="Search customers..." value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)} data-testid="search-input"
              className="w-full pl-10 pr-10 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
            {searchQuery.trim() && (
              <button onClick={() => { openAddModal(searchQuery.trim()); setSearchQuery(''); }}
                data-testid="search-add-btn" title="Create customer with this name"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-orange-500 hover:text-orange-600 hover:bg-orange-50 rounded transition-colors">
                <Plus className="h-5 w-5" />
              </button>
            )}
          </div>
          <select value={registrationFilter} onChange={e => setRegistrationFilter(e.target.value)}
            data-testid="filter-registration"
            className="px-3 py-2 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none bg-white min-w-[160px]">
            <option value="">All</option>
            <option value="Registered">Registered</option>
            <option value="Unregistered">Unregistered</option>
          </select>
          {registrationFilter && (
            <button onClick={() => setRegistrationFilter('')} data-testid="clear-filters-btn"
              className="text-xs text-red-500 hover:text-red-600 font-medium px-2">Clear</button>
          )}
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-6">
          <AlertCircle className="h-5 w-5" /><span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto"><X className="h-4 w-4" /></button>
        </div>
      )}

      <div className="bg-white rounded-sm border border-slate-200">
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16" data-testid="empty-state">
            <UserCheck className="h-16 w-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No customers yet</h3>
            <p className="text-slate-500 mb-6">Start by adding your first customer</p>
            <button onClick={() => openAddModal()}
              className="inline-flex items-center gap-2 bg-orange-500 text-white font-bold tracking-wider uppercase px-6 py-3 rounded-none hover:bg-orange-600 transition-colors">
              <Plus className="h-5 w-5" /> Add First Customer
            </button>
          </div>
        ) : (
          <>
            <div className="hidden md:grid grid-cols-8 gap-4 px-6 py-4 border-b border-slate-200 bg-slate-50">
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider col-span-2">Name</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Email</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Phone</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Registration</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">GSTIN</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Status</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Actions</div>
            </div>
            {filtered.map((item) => (
              <div key={item.id} data-testid={`customer-row-${item.id}`}
                className="grid grid-cols-1 md:grid-cols-8 gap-4 px-6 py-4 border-b border-slate-100 hover:bg-slate-50 items-center">
                <div className="col-span-2 font-medium text-slate-900">{item.name}</div>
                <div className="text-slate-600 text-sm">{item.email || '-'}</div>
                <div className="text-slate-600 text-sm">{item.phone || '-'}</div>
                <div>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                    (item.registration || 'Registered') === 'Registered'
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-slate-100 text-slate-600'
                  }`}>
                    {item.registration || 'Registered'}
                  </span>
                </div>
                <div className="text-slate-600 text-sm font-mono">{item.gst_number || '-'}</div>
                <div>
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                    item.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {item.active ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                    {item.active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <>
                  {perms.canEdit && (
                  <button onClick={() => openEditModal(item)} data-testid={`edit-btn-${item.id}`}
                    className="p-2 text-slate-600 hover:text-orange-500 hover:bg-orange-50 rounded transition-colors"><Pencil className="h-4 w-4" /></button>
                  )}
                  {perms.canDelete && (
                  <button onClick={() => setDeleteConfirm(item.id)} data-testid={`delete-btn-${item.id}`}
                    className="p-2 text-slate-600 hover:text-red-500 hover:bg-red-50 rounded transition-colors"><Trash2 className="h-4 w-4" /></button>
                  )}
                  </>
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-slate-900 mb-4">Confirm Delete</h3>
            <p className="text-slate-600 mb-6">Are you sure you want to delete this customer?</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50">Cancel</button>
              {perms.canDelete && (
              <button onClick={() => handleDelete(deleteConfirm)} data-testid="confirm-delete-btn" className="px-4 py-2 bg-red-500 text-white rounded-sm hover:bg-red-600">Delete</button>
              )}
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-sm w-full max-w-lg my-8">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-900 uppercase tracking-tight">
                {editingItem ? 'Edit Customer' : 'Add New Customer'}
              </h2>
              <button onClick={() => setShowModal(false)} className="p-2 text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-6">
              {error && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-6">
                  <AlertCircle className="h-5 w-5" /><span>{error}</span>
                </div>
              )}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Name <span className="text-red-500">*</span></label>
                  <input type="text" name="name" value={formData.name} onChange={handleInputChange} required
                    data-testid="input-name" className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                  <input type="email" name="email" value={formData.email} onChange={handleInputChange}
                    data-testid="input-email" className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
                  <input type="text" name="phone" value={formData.phone}
                    onChange={e => setFormData(prev => ({ ...prev, phone: e.target.value.replace(/[^0-9+\- ]/g, '') }))}
                    data-testid="input-phone" inputMode="numeric"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Registration <span className="text-red-500">*</span></label>
                  <div className="flex gap-3">
                    {['Registered', 'Unregistered'].map(opt => (
                      <label key={opt} className={`flex items-center gap-2 px-4 py-2 border rounded-sm cursor-pointer text-sm font-medium transition-colors ${
                        formData.registration === opt
                          ? 'border-orange-500 bg-orange-50 text-orange-700'
                          : 'border-slate-200 text-slate-600 hover:border-slate-300'
                      }`}>
                        <input type="radio" name="registration" value={opt} checked={formData.registration === opt}
                          onChange={handleInputChange} className="sr-only" />
                        {opt}
                      </label>
                    ))}
                  </div>
                </div>
                {formData.registration !== 'Unregistered' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">GSTIN</label>
                  <input type="text" name="gst_number" value={formData.gst_number} onChange={handleInputChange}
                    data-testid="input-gst" className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
                </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Billing Address</label>
                  <textarea name="billing_address" value={formData.billing_address} onChange={handleInputChange}
                    rows={2} placeholder="Billing address" data-testid="input-billing-address"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none resize-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Shipping Address</label>
                  <textarea name="shipping_address" value={formData.shipping_address} onChange={handleInputChange}
                    rows={2} placeholder="Shipping address" data-testid="input-shipping-address"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none resize-none" />
                </div>
                <div className="flex items-center gap-3">
                  <input type="checkbox" id="active" name="active" checked={formData.active} onChange={handleInputChange}
                    data-testid="input-active" className="w-5 h-5 text-orange-500 border-slate-300 rounded focus:ring-orange-500" />
                  <label htmlFor="active" className="text-sm font-medium text-slate-700">Active</label>
                </div>
                {!editingItem && (
                  <div className="flex items-center gap-3 p-3 bg-orange-50 border border-orange-200 rounded-sm">
                    <input type="checkbox" id="also_vendor" name="_also_create_vendor" checked={formData._also_create_vendor} onChange={handleInputChange}
                      data-testid="copy-to-vendor" className="w-5 h-5 text-orange-500 border-slate-300 rounded focus:ring-orange-500" />
                    <label htmlFor="also_vendor" className="text-sm font-medium text-slate-700">Also create as Vendor with the same details</label>
                  </div>
                )}
              </div>
              <div className="flex gap-3 justify-end pt-6 mt-6 border-t border-slate-200">
                <button type="button" onClick={() => setShowModal(false)}
                  className="px-6 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50">Cancel</button>
                <button type="submit" disabled={submitting} data-testid="submit-btn"
                  className="px-6 py-2 bg-orange-500 text-white font-bold uppercase tracking-wider rounded-sm hover:bg-orange-600 disabled:opacity-50 inline-flex items-center gap-2">
                  {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                  {editingItem ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
